import { createMemberAPI } from "../api/apiFactory";
import { isSystemAccount } from "../core/auth";
import { warn } from "../core/logger";
import { getMemberInactiveStatus } from "../utils/memberStatus";
import type {
  AcceptTermsResponse,
  EndMembershipResponse,
  IMemberAPI,
  ReactivateMemberResponse,
  RevokeEndMembershipResponse,
  RevokeSuspensionResponse,
  RevokeTerminationResponse,
  SuspendMemberResponse,
  TerminateMemberResponse,
} from "../api/memberApi";
import type { DeletionPreview, PermanentDeletionResponse } from "../types/MemberDeletion";
import type { Member, MemberFilters, MemberLoginResponse, MemberSort } from "../types/Member";
import type { MessageableEntity, MessageableMember } from "../types/MessageableEntity";
import type { PaginatedResponse } from "../types/Pagination";

/**
 * Service layer provides higher-level abstractions over the raw API.
 * Use this for:
 * - Client-side filtering/transformations
 * - Combining multiple API calls
 * - Business logic that doesn't belong in the API layer
 * - Caching strategies
 */
export class MemberService {
  public api: IMemberAPI;

  private _memberCache: Member[] | null = null;

  constructor(api?: IMemberAPI) {
    this.api = api || createMemberAPI();
  }

  private applyClientFilters(members: Member[], filters: MemberFilters): Member[] {
    return members.filter((member) => {
      if (filters.memberId && member.id !== filters.memberId) return false;
      if (filters.permissableLocationIds && !member.permissableLocationIds?.includes(filters.permissableLocationIds))
        return false;
      if (
        filters.practiceManagerLocationIds &&
        !member.practiceManagerLocationIds?.includes(filters.practiceManagerLocationIds)
      )
        return false;
      if (filters.email && member.email !== filters.email) return false;
      if (filters.lastName && member.lastName !== filters.lastName) return false;
      if (filters.active !== undefined && member.active !== filters.active) return false;
      if (filters.hasMfa !== undefined && member.hasMfa !== filters.hasMfa) return false;
      if (filters.isAdmin && member.role?.includes("admin") !== true) return false;
      if (filters.isPracticeManager && member.role?.includes("practice_manager") !== true) return false;
      // Prospects are identified by role now that membershipType is gone.
      if (filters.isProspect && member.role?.includes("prospect") !== true) return false;
      // The "talking" filter matches both pure talking_therapist members AND those who do
      // "both"; same for "body". Legacy DB values are normalised to the new union by
      // MemberTypeSchema on read, so we only need to compare against the new names here.
      if (filters.isTherapist) {
        if (member.type !== "talking_therapist" && member.type !== "both") return false;
      }
      if (filters.isBodyworker) {
        if (member.type !== "body_therapist" && member.type !== "both") return false;
      }

      if (filters.isPendingTermination) {
        // Recognise BOTH the legacy standalone-terminate fields AND the current
        // End-Membership flow (pendingMembershipEnd/membershipEndDate). Members queued
        // via today's UI use the latter, so the legacy-only check hid them.
        const legacyPending =
          !!member.scheduledTerminationId && !!member.terminationDate;
        const endMembershipPending =
          member.pendingMembershipEnd === true && !!member.membershipEndDate;
        const pending = (legacyPending || endMembershipPending) && member.isTerminated !== true;
        if (!pending) return false;
      }

      if (filters.isTerminated && member.isTerminated !== true) return false;

      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
        const nameMatch = fullName.includes(searchLower);
        const emailMatch = member.email.toLowerCase().includes(searchLower);
        if (!nameMatch && !emailMatch) return false;
      }

      return true;
    });
  }

  async loginMember(): Promise<MemberLoginResponse | Error> {
    const loginResult = await this.api.loginMember();
    if (loginResult instanceof Error) {
      warn("MemberService", `loginMember: Login failed: ${loginResult.message}`);
      return new Error("Login failed");
    }
    return loginResult;
  }

  async getMember() {
    const member = await this.api.fetchMember();
    return member;
  }

  async quickMemberById(
    id: string,
  ): Promise<{ memberId: string; email: string; firstName: string; lastName: string; avatar?: string; role?: string[]; practiceManagerLocations?: Array<{ id: string; name: string }> } | null> {
    const member = await this.api.fetchMemberById(id);
    if (!member) return null;
    return {
      memberId: member.id,
      email: member.email,
      firstName: member.firstName,
      lastName: member.lastName,
      avatar: member.avatar,
      role: member.role,
      practiceManagerLocations: member.practiceManagerLocations?.map((loc: any) => ({ id: loc.id, name: loc.name })),
    };
  }

  async getMembersOfPractice(
    permissableLocationIds: string[],
    filters?: MemberFilters,
    sort?: MemberSort,
    skip?: number,
    limit?: number,
  ): Promise<PaginatedResponse<Member>> {
    // Pass searchTerm, skip, limit AND status filters server-side so pagination stays correct
    // (filtering only the current page client-side would miss matches on other pages).
    const response = await this.api.fetchMembersOfPractice(
      permissableLocationIds,
      filters?.searchTerm,
      skip,
      limit,
      {
        active: filters?.active,
        isTerminated: filters?.isTerminated,
        isPendingTermination: filters?.isPendingTermination,
      },
      sort,
    );
    let membersOfPractice = response.data;

    // Apply remaining client-side filters (excluding searchTerm which is handled server-side)
    if (filters) {
      const clientFilters = { ...filters };
      delete clientFilters.searchTerm;
      if (Object.keys(clientFilters).length > 0) {
        membersOfPractice = this.applyClientFilters(membersOfPractice, clientFilters);
      }
    }

    return {
      ...response,
      data: membersOfPractice,
    };
  }

  async searchMemberByEmail(email: string): Promise<Member | null> {
    const response = await this.api.fetchSerializedMembers();
    return response.data.find((member) => member.email.toLowerCase() === email.toLowerCase()) || null;
  }

  async getSerializedMembers(): Promise<PaginatedResponse<Member>> {
    return this.api.fetchSerializedMembers();
  }

  async updateMemberSettings(
    memberId: string,
    settings: Partial<{
      theme: "light" | "dark";
      calendarTypePreference: "day" | "week";
      discoModeEnabled: boolean;
      calendarWidth: number;
      allowAnimation: boolean;
    }>,
  ): Promise<Member | Error> {
    // Only update the settings object, not the entire member
    const member = await this.api.fetchMemberById(memberId);
    if (!member) {
      return new Error(`Member with ID ${memberId} not found`);
    }

    // Merge with existing settings
    const updatedSettings = {
      theme: settings.theme ?? member.settings?.theme ?? "light",
      calendarTypePreference: settings.calendarTypePreference ?? member.settings?.calendarTypePreference ?? "day",
      discoModeEnabled: settings.discoModeEnabled ?? member.settings?.discoModeEnabled ?? false,
      calendarWidth: settings.calendarWidth ?? member.settings?.calendarWidth ?? 500,
      allowAnimation: settings.allowAnimation ?? member.settings?.allowAnimation ?? true,
      bodyFont: (settings as any).bodyFont ?? member.settings?.bodyFont ?? "EB Garamond",
    };

    return this.api.updateMember(memberId, { settings: updatedSettings });
  }

  async updateMember(memberId: string, updates: Partial<Member>): Promise<Member | Error> {
    return this.api.updateMember(memberId, updates);
  }

  // admin only
  async getAllMembers(
    filters?: MemberFilters,
    sort?: MemberSort,
    skip?: number,
    limit?: number,
  ): Promise<PaginatedResponse<Member>> {
    // Pass searchTerm, location filter, status (active/terminated/pending-termination), skip, and
    // limit to the server so pagination counts stay correct for every filter.
    const response = await this.api.fetchSerializedMembers(
      filters?.searchTerm,
      skip,
      limit,
      filters?.permissableLocationIds,
      {
        active: filters?.active,
        isTerminated: filters?.isTerminated,
        isPendingTermination: filters?.isPendingTermination,
      },
      sort,
    );
    let members = response.data;

    // Apply remaining client-side filters (excluding server-side handled filters)
    if (filters) {
      const clientFilters = { ...filters };
      delete clientFilters.searchTerm;
      delete clientFilters.permissableLocationIds;
      delete clientFilters.isPendingTermination;
      delete clientFilters.active;
      delete clientFilters.isTerminated;
      if (Object.keys(clientFilters).length > 0) {
        members = this.applyClientFilters(members, clientFilters);
      }
    }
    return {
      ...response,
      data: members,
    };
  }

  /**
   * Fetch the ENTIRE filtered member list (no pagination) for CSV export. Hits the audited
   * /members/export endpoint so every extraction of member PII is recorded in the audit trail.
   * Applies the same server-side filters (searchTerm, location, status) as the on-screen list, plus
   * any remaining client-side filters, so the export matches exactly what the user sees.
   */
  async exportMembers(filters?: MemberFilters): Promise<Member[]> {
    let members = await this.api.exportMembers(
      filters?.searchTerm,
      filters?.permissableLocationIds,
      {
        active: filters?.active,
        isTerminated: filters?.isTerminated,
        isPendingTermination: filters?.isPendingTermination,
      },
    );

    if (filters) {
      const clientFilters = { ...filters };
      delete clientFilters.searchTerm;
      delete clientFilters.permissableLocationIds;
      delete clientFilters.isPendingTermination;
      delete clientFilters.active;
      delete clientFilters.isTerminated;
      if (Object.keys(clientFilters).length > 0) {
        members = this.applyClientFilters(members, clientFilters);
      }
    }
    return members;
  }

  async getMemberById(memberId: string): Promise<Member | null> {
    return this.api.fetchMemberById(memberId);
  }

  /**
   * Bulk fetch many members in a single request. Returns only the members that exist plus a
   * list of IDs that didn't resolve, so callers (e.g. OrganisationMembersDialog) can surface
   * orphan references to admins rather than having one 404 nuke the whole list.
   */
  async getMembersByIds(memberIds: string[]): Promise<{ members: Member[]; missingIds: string[] }> {
    return this.api.fetchMembersByIds(memberIds);
  }

  // admin only
  async cacheAllMembers(): Promise<Member[]> {
    if (this._memberCache) return this._memberCache;
    const response = await this.api.fetchSerializedMembers();
    this._memberCache = response.data;
    return this._memberCache;
  }

  // admin only
  async findMembers(searchTerm: string, includeSystemAccounts?: boolean): Promise<Member[]> {
    if (!searchTerm || searchTerm.trim() === "") {
      return [];
    }

    // Server-side search: the backend matches firstName / lastName / email / businessName /
    // "first last" full name, across ALL centres (PM/admin are elevated → unscoped). Pass a high
    // limit so matches aren't capped to the first page (the default page size is 200).
    const response = await this.api.fetchSerializedMembers(searchTerm.trim(), 0, 1000);
    let members = response.data;

    // Exclude terminated and inactive members. Members merely QUEUED for end-membership
    // (state "ending") are still active and manageable — e.g. elevated book-on-behalf — so
    // keep them in the results; only truly-inactive states are filtered out.
    members = members.filter((member) => {
      const status = getMemberInactiveStatus(member);
      return status === null || status.state === "ending";
    });

    if (!includeSystemAccounts) {
      members = members.filter((member) => !isSystemAccount(member));
    }

    return members;
  }

  // admin only
  clearMemberCache(): void {
    this._memberCache = null;
  }

  // pm/admin only
  async addMemberToLocation(
    memberId: string,
    locationId: string,
  ): Promise<{ message: string; success: boolean } | Error> {
    return this.api.addMemberToLocation(memberId, locationId);
  }

  async getMembersICanMessage(searchTerm?: string): Promise<MessageableEntity[]> {
    let entities = await this.api.fetchMembersICanMessage();

    // Apply search filter if provided
    if (searchTerm && searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase().trim();
      entities = entities.filter((entity: MessageableEntity) => {
        if (entity.type === "member") {
          const fullName = `${entity.firstName ?? ""} ${entity.lastName ?? ""}`.toLowerCase();
          return (
            fullName.includes(lowerSearch) ||
            entity.email?.toLowerCase().includes(lowerSearch)
          );
        } else {
          // For groups, search by group name
          return entity.name?.toLowerCase().includes(lowerSearch);
        }
      });
    }

    return entities;
  }

  private memberToEntity(member: Member): MessageableMember {
    return {
      type: "member",
      id: member.id,
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      active: member.active,
      role: member.role,
      avatar: member.avatar,
      businessName: member.businessName,
      permissableLocations: member.permissableLocations,
      practiceManagerLocations: member.practiceManagerLocations,
      organisationName: member.organisation?.name,
    };
  }

  async searchActiveMembers(searchTerm?: string): Promise<MessageableEntity[]> {
    // excludeUnassignable=true: the backend drops members flagged not-assignable for non-admin
    // callers, so restricted members never appear in the on-behalf booking picker.
    const result = await this.api.fetchSerializedMembers(searchTerm, undefined, undefined, undefined, undefined, undefined, true);
    return result.data
      .filter((m) => m.active)
      .map((m) => this.memberToEntity(m));
  }

  async searchMembersByLocation(locationId: string, searchTerm?: string): Promise<MessageableEntity[]> {
    const members = await this.api.fetchMembersByLocation(locationId, searchTerm);
    return members
      .filter((m) => m.active)
      .map((m) => this.memberToEntity(m));
  }

  async getMembersByLocation(locationId: string, searchTerm?: string): Promise<Member[]> {
    return this.api.fetchMembersByLocation(locationId, searchTerm);
  }

  /**
   * Membership lifecycle changes (suspend/terminate/end and their reversals) cascade
   * booking cancellations or reinstatements on the server. The calendar and booking
   * lists read from a separate React Query cache keyed under ["bookings", ...], so we
   * must invalidate booking queries after a successful change — otherwise cancelled
   * bookings linger on the calendar until the poll/staleTime elapses, eroding trust.
   * Only invalidate on success: the API layer returns an Error value on failure.
   */
  private async invalidateBookingsAfterLifecycleChange(result: unknown): Promise<void> {
    if (result instanceof Error) return;
    const { invalidateBookingQueries } = await import("@/lib/queryClient");
    await invalidateBookingQueries();
  }

  // Suspension methods
  async suspendMember(memberId: string, suspensionEndDate: string): Promise<SuspendMemberResponse | Error> {
    const result = await this.api.suspendMember(memberId, suspensionEndDate);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  async revokeSuspension(memberId: string): Promise<RevokeSuspensionResponse | Error> {
    const result = await this.api.revokeSuspension(memberId);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  // Termination methods
  async terminateMember(memberId: string, terminationDate: string): Promise<TerminateMemberResponse | Error> {
    const result = await this.api.terminateMember(memberId, terminationDate);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  async revokeTermination(memberId: string): Promise<RevokeTerminationResponse | Error> {
    const result = await this.api.revokeTermination(memberId);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  async reactivateMember(memberId: string): Promise<ReactivateMemberResponse | Error> {
    const result = await this.api.reactivateMember(memberId);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  // End Membership — unified flow replacing standalone Suspend + Terminate.
  async endMembership(memberId: string, endDate?: string): Promise<EndMembershipResponse | Error> {
    const result = await this.api.endMembership(memberId, endDate);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  async revokeEndMembership(memberId: string): Promise<RevokeEndMembershipResponse | Error> {
    const result = await this.api.revokeEndMembership(memberId);
    await this.invalidateBookingsAfterLifecycleChange(result);
    return result;
  }

  // Terms acceptance
  async acceptTerms(memberId: string, termsVersion: number): Promise<AcceptTermsResponse | Error> {
    return this.api.acceptTerms(memberId, termsVersion);
  }

  async acceptAnnualDeclaration(memberId: string): Promise<AcceptTermsResponse | Error> {
    return this.api.acceptAnnualDeclaration(memberId);
  }

  // Permanent deletion methods
  async previewPermanentDeletion(memberId: string): Promise<DeletionPreview | Error> {
    return this.api.previewPermanentDeletion(memberId);
  }

  async permanentlyDeleteMember(
    memberId: string,
    confirmText: string,
    reason?: string
  ): Promise<PermanentDeletionResponse | Error> {
    return this.api.permanentlyDeleteMember(memberId, confirmText, reason);
  }

  /**
   * SENSITIVE, AUDITED, ON-DEMAND: reveals the member's current unexpired login OTP (if any).
   * The backend audits every call, so this must only be invoked from an explicit user action
   * (see useLastLoginCode / LastLoginCodePanel) — never fetched automatically on view load.
   */
  async getLastLoginCode(memberId: string): Promise<{ code: string; expiresAt: number } | { code: null }> {
    return this.api.getLastLoginCode(memberId);
  }

  // this stops ts complaining about the unused private field
  async noop(): Promise<void> {
    console.log(this.api);
    return;
  }
}
